/**
 * BizInfoContext.jsx
 * 
 * Fetches /api/admin/settings ONCE when the admin is logged in
 * and makes bizInfo available to any component via useBizInfo().
 * 
 * Usage:
 *   1. Wrap your admin layout/router with <BizInfoProvider>
 *   2. In any component: const { bizInfo } = useBizInfo();
 *   3. Pass to OrderBill: <OrderBill bizInfo={bizInfo} ... />
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext'; // adjust path if needed

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BizInfoContext = createContext({
  bizInfo: {},
  refreshBizInfo: () => {},
  loading: false,
});

export function BizInfoProvider({ children }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [bizInfo, setBizInfo] = useState({
    business_name:    '',
    business_address: '',
    business_phone:   '',
    business_email:   '',
    gstin:            '',
  });
  const [loading, setLoading] = useState(false);

  const fetchBizInfo = useCallback(async () => {
    if (!token || !isAdmin) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBizInfo({
        business_name:    data.business_name    || '',
        business_address: data.business_address || '',
        business_phone:   data.business_phone   || '',
        business_email:   data.business_email   || '',
        gstin:            data.gstin            || '',
      });
    } catch (err) {
      console.error('BizInfoContext: failed to fetch settings', err);
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  // Fetch once on mount / when admin logs in
  useEffect(() => {
    fetchBizInfo();
  }, [fetchBizInfo]);

  return (
    <BizInfoContext.Provider value={{ bizInfo, refreshBizInfo: fetchBizInfo, loading }}>
      {children}
    </BizInfoContext.Provider>
  );
}

export function useBizInfo() {
  return useContext(BizInfoContext);
}
